/**
 * MenuScene — título e navegação em TRÊS páginas (pedido do playtest):
 * UM JOGADOR (clássico/diário/seed) · MULTIPLAYER (co-op local/corrida
 * online) · OPÇÕES (som/CRT/autofire). ESC ou VOLTAR sobe um nível.
 *
 * Login Pixelforge e rankings entram quando a integração com o portal for
 * feita junto do usuário (skills login-compartilhado/publicar-na-vm).
 * Opções persistem em localStorage com prefixo `pixelraid:` (contrato
 * pf-save, §19). O logo pixel art substitui o texto quando for gerado e
 * aprovado no pipeline.
 *
 * Teclas por EVENTO (`keydown-X`): JustDown/Key.on não disparam no Phaser 4.
 */

import Phaser from 'phaser'
import { SCREEN, dailySeed } from '@pixelraid/core'
import { onKey } from '../input/keys'
import { RiverView, generatorSpans } from '../terrain/river'
import { TOUCH_MIN } from '../ui/buttons'
import { VOLUME_STEPS, loadVolume, saveVolume } from '../systems/AudioSystem'
import { loadRecords } from '../systems/records'
import { fetchSeason, fetchTop, type SeasonInfo, type TopRow } from '../systems/season-client'
import { OutlinedText, pixelText, pixelTitle, preloadPixelFonts, setPixelText } from '../ui/text'

/** Seed fixa do modo Clássico — publicada, igual para todo mundo (§12). */
export const CLASSIC_SEED = 20260819

/** Atrás de todo o texto do menu, que vive na profundidade 0. */
const MENU_BACKDROP_DEPTH = -10

/**
 * Rolagem do fundo, em px/s. O jogo cruza a 120 (`SCROLL.CRUISE`); aqui é
 * deriva, não voo — rápido demais compete com a leitura do menu.
 */
const MENU_SCROLL_PX_S = 28

/** Pula a abertura reta do mundo, que dá uma primeira tela sem graça. */
const MENU_SCROLL_START_PX = 1600

/** Opacidade do véu sobre o cenário. Sobe se o menu ficar difícil de ler. */
const MENU_BACKDROP_DIM = 0.55

declare global {
  interface Window {
    __pixelraidMenu?: { ready: boolean; page?: string; cursor?: number }
  }
}

type Page = 'root' | 'single' | 'multi' | 'season' | 'options'

interface MenuItem {
  label: () => string
  action: () => void
}

export class MenuScene extends Phaser.Scene {
  private page: Page = 'root'
  private items: MenuItem[] = []
  private itemTexts: Phaser.GameObjects.BitmapText[] = []
  private cursor = 0
  private cursorText!: Phaser.GameObjects.BitmapText
  private subtitleText!: OutlinedText
  private hintText!: OutlinedText
  private river!: RiverView
  private scrollPx = MENU_SCROLL_START_PX
  /** Placar da pista sob o cursor, na página de season. */
  private boardText!: Phaser.GameObjects.BitmapText
  private season: SeasonInfo | null = null
  /** Cache por pista: andar com o cursor não pode virar rajada de rede. */
  private readonly boardCache = new Map<number, TopRow[]>()

  constructor() {
    super({ key: 'menu', active: false })
  }

  preload(): void {
    this.load.setBaseURL(import.meta.env.BASE_URL)
    preloadPixelFonts(this)
    // Só o necessário para o cenário de fundo — nenhum sprite de entidade.
    // O guard evita o aviso de chave duplicada ao voltar do jogo, que já as
    // carregou.
    for (const key of ['tileset_river', 'deco_tree', 'deco_house']) {
      if (this.textures.exists(key) === false) this.load.image(key, `assets/sprites/${key}.png`)
    }
  }

  create(): void {
    const centerX = SCREEN.WIDTH / 2

    // Pano de fundo: o rio do modo Clássico rolando devagar, tela cheia e sem
    // nenhuma entidade — é cenário, não partida. Profundidade negativa para
    // ficar atrás de todo o texto do menu.
    this.river = new RiverView(this, {
      seed: CLASSIC_SEED,
      spanAt: generatorSpans(CLASSIC_SEED),
      height: SCREEN.HEIGHT,
      depth: MENU_BACKDROP_DEPTH,
    })
    // Começa adiantado: as primeiras linhas do mundo são a abertura reta, que
    // renderia um fundo sem graça bem na primeira tela que o jogador vê.
    this.scrollPx = MENU_SCROLL_START_PX
    this.river.draw(this.scrollPx)

    // Véu escuro entre cenário e texto. NÃO é elemento de jogo: é o que
    // impede o fundo de desfazer a legibilidade do menu — texto claro sobre a
    // margem verde some, e foi exatamente disso que o playtest 3 reclamou.
    this.add
      .rectangle(0, 0, SCREEN.WIDTH, SCREEN.HEIGHT, 0x000814, MENU_BACKDROP_DIM)
      .setOrigin(0, 0)
      .setDepth(MENU_BACKDROP_DEPTH + 2)

    pixelTitle(this, centerX, 56, 32, 'PIXEL RAID', '#f2f2f2', '#ff8c1a')
    // 12px é o MAIOR que cabe: medido, o subtítulo em 16px dá 257px e estoura
    // os 256 da tela. O contorno é o que compensa o tamanho — sem ele, texto
    // miúdo sobre o cenário que rola some, com qualquer cor.
    this.subtitleText = new OutlinedText(this, centerX, 86, 12, '', {
      color: '#b9f5d0',
      outline: '#04140c',
    })

    this.cursorText = pixelText(this, 24, 140, 16, '>', { color: '#ff8c1a' })

    this.boardText = pixelText(this, centerX, 270, 10, '', {
      color: '#ffd23c',
      align: 'center',
      origin: [0.5, 0],
    })

    this.hintText = new OutlinedText(this, centerX, 330, 12, '', {
      color: '#ffffff',
      outline: '#06121c',
      align: 'center',
    })

    this.showPage('root')

    // Busca a season sem bloquear o menu: ele já está utilizável antes da
    // resposta, e a página de season se redesenha quando ela chega.
    void fetchSeason().then((info) => {
      if (!this.scene.isActive()) return
      this.season = info
      if (this.page === 'season') this.showPage('season')
    })

    const keyboard = this.input.keyboard!
    onKey(keyboard, 'keydown-UP', () => this.move(-1))
    onKey(keyboard, 'keydown-DOWN', () => this.move(1))
    onKey(keyboard, 'keydown-W', () => this.move(-1))
    onKey(keyboard, 'keydown-S', () => this.move(1))
    onKey(keyboard, 'keydown-ENTER', () => this.items[this.cursor]?.action())
    onKey(keyboard, 'keydown-SPACE', () => this.items[this.cursor]?.action())
    onKey(keyboard, 'keydown-ESC', () => {
      if (this.page !== 'root') this.showPage('root')
    })

    // Gancho de teste: o e2e só digita depois que o menu está ouvindo.
    window.__pixelraidMenu = { ready: true, page: this.page }
    this.events.once('shutdown', () => {
      window.__pixelraidMenu = { ready: false }
    })
  }

  override update(_time: number, delta: number): void {
    // Rolagem por tempo real: isto é enfeite de cena, não simulação — pode
    // usar o relógio do navegador à vontade. Quem não pode é o core.
    this.scrollPx += (MENU_SCROLL_PX_S * delta) / 1000
    this.river.draw(this.scrollPx)
  }

  private flag(key: string, fallback: boolean): boolean {
    const raw = localStorage.getItem(key)
    return raw === null ? fallback : raw === '1'
  }

  private toggle(key: string): void {
    localStorage.setItem(key, this.flag(key, false) ? '0' : '1')
    this.refreshLabels()
  }

  private pageItems(page: Page): { subtitle: string; items: MenuItem[] } {
    const onOff = (value: boolean): string => (value ? 'LIGADO' : 'DESLIGADO')
    switch (page) {
      case 'root':
        return {
          subtitle: 'um tributo aos shooters fluviais',
          items: [
            { label: () => 'UM JOGADOR', action: () => this.showPage('single') },
            { label: () => 'MULTIPLAYER', action: () => this.showPage('multi') },
            { label: () => 'SEASON', action: () => this.showPage('season') },
            { label: () => 'OPÇÕES', action: () => this.showPage('options') },
          ],
        }
      case 'single': {
        // O recorde aparece aqui porque é onde o jogador escolhe jogar de
        // novo — número guardado que ninguém vê não motiva ninguém.
        const best = loadRecords().classic
        return {
          subtitle:
            best === null
              ? 'nenhum recorde no clássico ainda'
              : `recorde clássico: ${String(best.distPx).padStart(6, '0')}m`,
          items: [
            { label: () => 'JOGAR  (clássico)', action: () => this.startGame(CLASSIC_SEED, null) },
            {
              label: () => `DESAFIO DIÁRIO  ${new Date().toISOString().slice(0, 10)}`,
              action: () => {
                const today = new Date().toISOString().slice(0, 10)
                this.startGame(dailySeed(today), today)
              },
            },
            {
              label: () => 'SEED ALEATÓRIA',
              action: () => this.startGame((Math.random() * 0xffffffff) >>> 0, null),
            },
            { label: () => 'VOLTAR', action: () => this.showPage('root') },
          ],
        }
      }
      case 'season': {
        const info = this.season
        if (info === null) {
          // Sem servidor a season simplesmente não existe para este jogador —
          // e o resto do jogo continua inteiro. Bônus, nunca pedágio.
          return {
            subtitle: 'season indisponível',
            items: [{ label: () => 'VOLTAR', action: () => this.showPage('root') }],
          }
        }
        return {
          subtitle: `season ${info.season} · até ${info.endsOn.slice(8)}/${info.endsOn.slice(5, 7)}`,
          items: [
            ...info.tracks.map((seed, i) => ({
              label: () => `PISTA ${i + 1}`,
              action: () => this.scene.start('game', { seed, daily: null, seasonTrack: i }),
            })),
            { label: () => 'VOLTAR', action: () => this.showPage('root') },
          ],
        }
      }
      case 'multi':
        return {
          subtitle: 'multiplayer',
          items: [
            {
              // Co-op local de teclado (§12.1): setas = P1, WASD = P2.
              // Gamepads (2-4) entram com o lobby "pressione A" + hardware.
              label: () => 'CO-OP LOCAL  (2 no teclado)',
              action: () =>
                this.scene.start('game', {
                  seed: (Math.random() * 0xffffffff) >>> 0,
                  daily: null,
                  players: 2,
                }),
            },
            { label: () => 'CORRIDA ONLINE', action: () => this.scene.start('lobby', { mode: 'race' }) },
            { label: () => 'CO-OP ONLINE', action: () => this.scene.start('lobby', { mode: 'coop' }) },
            { label: () => 'VOLTAR', action: () => this.showPage('root') },
          ],
        }
      case 'options':
        return {
          subtitle: 'opções',
          items: [
            {
              label: () => `SOM: ${onOff(!this.flag('pixelraid:muted', false))}`,
              action: () => this.toggle('pixelraid:muted'),
            },
            {
              // Cicla e dá a volta. O menu é dirigido por ENTER — as setas já
              // são a navegação entre itens, então roubá-las para ajustar
              // valor quebraria a única regra que o jogador já aprendeu aqui.
              // Com 5 passos, dar a volta inteira custa 5 toques.
              label: () => `VOLUME: ${loadVolume() * (100 / VOLUME_STEPS)}%`,
              action: () => {
                saveVolume((loadVolume() + 1) % (VOLUME_STEPS + 1))
                this.refreshLabels()
              },
            },
            {
              label: () => `MODO CRT: ${onOff(this.flag('pixelraid:crt', false))}`,
              action: () => this.toggle('pixelraid:crt'),
            },
            {
              label: () => `AUTOFIRE: ${onOff(this.flag('pixelraid:autofire', false))}`,
              action: () => this.toggle('pixelraid:autofire'),
            },
            { label: () => 'VOLTAR', action: () => this.showPage('root') },
          ],
        }
    }
  }

  private showPage(page: Page): void {
    this.page = page
    this.cursor = 0
    for (const text of this.itemTexts) text.destroy()
    this.itemTexts = []

    const { subtitle, items } = this.pageItems(page)
    this.items = items
    this.subtitleText.setText(subtitle)
    // A dica acompanha a página: as teclas do co-op só interessam (e só cabem)
    // onde o co-op é escolhido.
    // Uma ideia por linha, e sem abreviação: "C crt · M som" era ilegível
    // mesmo nítido — o playtest 4 parou de ler exatamente ali.
    this.hintText.setText(
      page === 'multi'
        ? // Distingue os dois casos de propósito: dividir o teclado só vale no
          // co-op LOCAL. Nos modos online cada um tem o teclado inteiro, e a
          // dica antiga sugeria o contrário.
          'online: setas ou WASD\nco-op local divide o teclado\nP1 setas+ESPAÇO · P2 WASD+SHIFT'
        : page === 'season'
          // Uma linha só: a página tem 5 itens E o placar de 3 linhas embaixo.
          // Com as três linhas normais, a dica subiria por cima do placar.
          ? 'ENTER joga a pista · ESC volta'
          : 'setas ou WASD navegam · ENTER escolhe\nESC volta · ESPAÇO atira\nC liga o modo CRT · M liga o som',
    )

    this.itemTexts = items.map((item, index) => {
      const label = pixelText(this, 44, 140 + index * 26, 16, item.label(), { color: '#f2f2f2' })
      // Área de clique EXPLÍCITA: `BitmapText` não tem frame próprio, e sem
      // forma o Phaser cairia no `setHitAreaFromTexture` — que aqui é o atlas
      // inteiro (256px de largura), tornando clicável meia tela.
      //
      // A LINHA inteira, não a caixa do glifo. Com a caixa exata o alvo tinha
      // a altura do texto e a largura da palavra: num Pixel 7 emulado, um
      // toque 9px acima de "MULTIPLAYER" não fazia absolutamente nada, e o
      // menu parecia não responder ao dedo. A linha dá `TOUCH_MIN` de altura
      // e a tela toda de largura, e ainda deixa a mira do mouse igual.
      const alturaLinha = Math.max(TOUCH_MIN, label.height)
      label
        .setInteractive({
          hitArea: new Phaser.Geom.Rectangle(
            -44,
            (label.height - alturaLinha) / 2,
            SCREEN.WIDTH,
            alturaLinha,
          ),
          hitAreaCallback: Phaser.Geom.Rectangle.Contains,
          useHandCursor: true,
        })
        .on('pointerup', () => {
          this.cursor = index
          this.refreshCursor()
          item.action()
        })
      return label
    })
    this.refreshCursor()
    this.refreshBoard()
    if (window.__pixelraidMenu?.ready === true) window.__pixelraidMenu = { ready: true, page }
  }

  private move(delta: number): void {
    this.cursor = (this.cursor + delta + this.items.length) % this.items.length
    this.refreshCursor()
    this.refreshBoard()
    if (window.__pixelraidMenu?.ready === true) {
      window.__pixelraidMenu = { ready: true, page: this.page, cursor: this.cursor }
    }
  }

  /**
   * Placar da pista sob o cursor. Cacheado por pista: andar com o cursor não
   * pode virar rajada de requisição.
   */
  private refreshBoard(): void {
    const info = this.season
    if (this.page !== 'season' || info === null || this.cursor >= info.tracks.length) {
      setPixelText(this.boardText, '')
      return
    }
    const track = this.cursor
    const cached = this.boardCache.get(track)
    if (cached !== undefined) {
      this.renderBoard(cached)
      return
    }
    setPixelText(this.boardText, 'carregando placar...')
    void fetchTop(info.season, track, 3).then((rows) => {
      this.boardCache.set(track, rows)
      // O jogador pode ter saído da página enquanto a resposta vinha.
      if (this.scene.isActive() && this.page === 'season' && this.cursor === track) {
        this.renderBoard(rows)
      }
    })
  }

  private renderBoard(rows: TopRow[]): void {
    if (rows.length === 0) {
      setPixelText(this.boardText, 'ninguém pontuou\nnesta pista ainda')
      return
    }
    const linhas = rows.map(
      (row, i) => `${i + 1}. ${row.name.slice(0, 12)}  ${String(row.dist).padStart(6, '0')}m`,
    )
    setPixelText(this.boardText, linhas.join('\n'))
  }

  private refreshCursor(): void {
    this.cursorText.setY(140 + this.cursor * 26)
  }

  private refreshLabels(): void {
    for (let i = 0; i < this.items.length; i++) setPixelText(this.itemTexts[i]!, this.items[i]!.label())
  }

  private startGame(seed: number, daily: string | null): void {
    this.scene.start('game', { seed, daily })
  }
}
